/**
 * Question Bank Registry & Validator for Kazakan Cognitive Test
 * 56 Template Families across 7 Core Cognitive Domains + Practice Item
 */

import { ABSTRACT_TEMPLATES } from './templates-abstract.js';
import { QUANTITATIVE_TEMPLATES } from './templates-quantitative.js';
import { SPATIAL_TEMPLATES } from './templates-spatial.js';
import { VERBAL_TEMPLATES } from './templates-verbal.js';
import { MEMORY_TEMPLATES } from './templates-memory.js';
import { SPEED_TEMPLATES } from './templates-speed.js';
import { SOCIAL_TEMPLATES } from './templates-social.js';
import { DrawShapes, renderOptionSvg, render3x3Matrix } from './svg-builder.js';

import { ALL_EXPANDED_TEMPLATES } from './bank-expansion.js';

export const DOMAINS = [
  {
    id: 'abstract_reasoning',
    name_ar: 'الاستدلال المجرد والأنماط',
    name_en: 'Abstract Reasoning & Patterns',
    icon: 'puzzle',
    color: '#83C8F0'
  },
  {
    id: 'quantitative_reasoning',
    name_ar: 'التفكير الكمي والرياضي',
    name_en: 'Quantitative Reasoning',
    icon: 'math',
    color: '#6666E8'
  },
  {
    id: 'spatial_reasoning',
    name_ar: 'التصور البصري والمكاني',
    name_en: 'Spatial & Visual Reasoning',
    icon: 'cube',
    color: '#38bdf8'
  },
  {
    id: 'verbal_reasoning',
    name_ar: 'الاستدلال اللفظي والدلالي',
    name_en: 'Verbal & Linguistic Reasoning',
    icon: 'book',
    color: '#a78bfa'
  },
  {
    id: 'working_memory',
    name_ar: 'الذاكرة العاملة والاستبقاء',
    name_en: 'Working Memory',
    icon: 'brain',
    color: '#10b981'
  },
  {
    id: 'processing_speed',
    name_ar: 'الانتباه وسرعة المعالجة',
    name_en: 'Attention & Processing Speed',
    icon: 'bolt',
    color: '#f59e0b'
  },
  {
    id: 'practical_social',
    name_ar: 'الاستدلال العملي والاجتماعي',
    name_en: 'Practical & Social Reasoning',
    icon: 'users',
    color: '#ec4899'
  }
];

export const ALL_TEMPLATES = [
  ...ABSTRACT_TEMPLATES,
  ...QUANTITATIVE_TEMPLATES,
  ...SPATIAL_TEMPLATES,
  ...VERBAL_TEMPLATES,
  ...MEMORY_TEMPLATES,
  ...SPEED_TEMPLATES,
  ...SOCIAL_TEMPLATES,
  ...ALL_EXPANDED_TEMPLATES
];

/**
 * Normalizes text content for strict equivalence comparison
 */
function normalizeText(str) {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/\s+/g, ' ');
}

/**
 * Normalizes SVG strings by stripping non-visual attributes and whitespace
 */
function normalizeSvg(svg) {
  if (typeof svg !== 'string' || !svg) return '';
  return svg
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\s*(aria-label|aria-hidden|id|class)="[^"]*"/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Validates generated question item according to strict psychometric & uniqueness rules
 * Guarantees exactly one correct answer and zero duplicate answers across all options
 */
export function validateQuestionItem(item) {
  if (!item) return { valid: false, error: 'Item is null' };
  if (!item.templateId) return { valid: false, error: 'Missing templateId' };
  if (!item.questionText_ar || !item.questionText_en) return { valid: false, error: 'Missing bilingual question text' };
  if (!Array.isArray(item.options) || item.options.length < 2) return { valid: false, error: 'Invalid options array' };

  if (typeof item.correctIndex !== 'number' || !Number.isInteger(item.correctIndex) || item.correctIndex < 0 || item.correctIndex >= item.options.length) {
    return { valid: false, error: `Invalid correctIndex: ${item.correctIndex}` };
  }

  // 1. Verify correct option exists
  const correctOption = item.options[item.correctIndex];
  if (!correctOption) {
    return { valid: false, error: 'Correct option is undefined' };
  }

  // 2. If isCorrect flags are present, enforce strictly one is true matching correctIndex
  const correctFlagIndices = [];
  item.options.forEach((opt, idx) => {
    if (opt.isCorrect === true) correctFlagIndices.push(idx);
  });
  if (correctFlagIndices.length > 0) {
    if (correctFlagIndices.length > 1) {
      return { valid: false, error: `Multiple options marked as correct: [${correctFlagIndices.join(', ')}]` };
    }
    if (correctFlagIndices[0] !== item.correctIndex) {
      return { valid: false, error: `correctIndex ${item.correctIndex} does not match isCorrect index ${correctFlagIndices[0]}` };
    }
  }

  // 3. Strict Mutual Exclusivity and Pairwise Uniqueness
  const seenArText = new Set();
  const seenEnText = new Set();
  const seenSvg = new Set();
  const seenValues = new Set();
  const seenComposite = new Set();

  for (let i = 0; i < item.options.length; i++) {
    const opt = item.options[i];
    if (!opt) return { valid: false, error: `Option at index ${i} is null` };

    const normAr = normalizeText(opt.text_ar);
    const normEn = normalizeText(opt.text_en);
    const normSvg = normalizeSvg(opt.svg);
    const val = opt.value;

    if (!normAr && !normEn && !normSvg) {
      return { valid: false, error: `Option at index ${i} has no visual or textual content` };
    }

    // Check Arabic text uniqueness
    if (normAr) {
      if (seenArText.has(normAr)) {
        return { valid: false, error: `Duplicate Arabic option text detected: "${normAr}" at index ${i}` };
      }
      seenArText.add(normAr);
    }

    // Check English text uniqueness
    if (normEn) {
      if (seenEnText.has(normEn)) {
        return { valid: false, error: `Duplicate English option text detected: "${normEn}" at index ${i}` };
      }
      seenEnText.add(normEn);
    }

    // Check Visual SVG uniqueness
    if (normSvg) {
      if (seenSvg.has(normSvg)) {
        return { valid: false, error: `Duplicate visual SVG detected at option index ${i}` };
      }
      seenSvg.add(normSvg);
    }

    // Check explicit value uniqueness (if defined and non-empty)
    if (val !== undefined && val !== null && val !== '') {
      const valKey = typeof val === 'object' ? JSON.stringify(val) : String(val);
      if (seenValues.has(valKey)) {
        return { valid: false, error: `Duplicate option value detected: "${valKey}" at index ${i}` };
      }
      seenValues.add(valKey);
    }

    // Composite signature uniqueness
    const compositeSig = `${normAr}|${normEn}|${normSvg}`;
    if (seenComposite.has(compositeSig)) {
      return { valid: false, error: `Duplicate composite option signature at index ${i}` };
    }
    seenComposite.add(compositeSig);
  }

  if (!item.explanation_ar || !item.explanation_en) {
    return { valid: false, error: 'Missing explanation' };
  }

  return { valid: true };
}

/**
 * Ensures zero duplicate options exist within the generated item by resolving textual and numeric collisions
 */
function ensureUniqueOptions(item, prng) {
  if (!item || !Array.isArray(item.options) || item.options.length < 2) return item;

  const seenAr = new Set();
  const seenEn = new Set();
  const seenSvg = new Set();
  const seenVal = new Set();

  const cOpt = item.options[item.correctIndex];
  if (cOpt) {
    if (cOpt.text_ar) seenAr.add(normalizeText(cOpt.text_ar));
    if (cOpt.text_en) seenEn.add(normalizeText(cOpt.text_en));
    if (cOpt.svg) seenSvg.add(normalizeSvg(cOpt.svg));
    if (cOpt.value !== undefined && cOpt.value !== null && cOpt.value !== '') {
      seenVal.add(typeof cOpt.value === 'object' ? JSON.stringify(cOpt.value) : String(cOpt.value));
    }
  }

  item.options.forEach((opt, idx) => {
    if (idx === item.correctIndex) return;

    let normAr = normalizeText(opt.text_ar);
    let normEn = normalizeText(opt.text_en);
    let attempts = 0;

    // Resolve numeric text collision
    while ((seenAr.has(normAr) || seenEn.has(normEn)) && attempts < 10) {
      attempts++;
      if (normAr && !isNaN(Number(normAr))) {
        const num = Number(normAr);
        const shift = prng ? prng.pick([3, 5, 7, -3, -5, 9, -9]) : (attempts * 2 + 1);
        opt.text_ar = String(num + shift);
        opt.text_en = String(num + shift);
        if (opt.value !== undefined) opt.value = num + shift;
      } else if (normAr) {
        opt.text_ar = `${opt.text_ar} [${attempts}]`;
        opt.text_en = `${opt.text_en} [${attempts}]`;
      }
      normAr = normalizeText(opt.text_ar);
      normEn = normalizeText(opt.text_en);
    }

    if (normAr) seenAr.add(normAr);
    if (normEn) seenEn.add(normEn);
    if (opt.svg) seenSvg.add(normalizeSvg(opt.svg));
    if (opt.value !== undefined && opt.value !== null && opt.value !== '') {
      seenVal.add(typeof opt.value === 'object' ? JSON.stringify(opt.value) : String(opt.value));
    }
  });

  return item;
}

/**
 * Generate a validated question item with strict unique-answer guarantee and domain fallbacks
 */
export function generateQuestion(template, prng, difficulty = 2) {
  // 1. Primary generation attempts with requested difficulty
  for (let attempt = 0; attempt < 8; attempt++) {
    try {
      let item = template.generate(prng, difficulty);
      item = ensureUniqueOptions(item, prng);
      const validation = validateQuestionItem(item);
      if (validation.valid) {
        return item;
      }
    } catch (e) {
      // Continue to next attempt
    }
  }

  // 2. Secondary generation attempts with baseline difficulty 2
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      let item = template.generate(prng, 2);
      item = ensureUniqueOptions(item, prng);
      const validation = validateQuestionItem(item);
      if (validation.valid) {
        return item;
      }
    } catch (e) {}
  }

  // 3. Fallback to another verified template within the same cognitive domain
  const domainPeers = ALL_TEMPLATES.filter(t => t.domain === template.domain && t.id !== template.id);
  for (const altTemplate of domainPeers) {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        let item = altTemplate.generate(prng, 2);
        item = ensureUniqueOptions(item, prng);
        const validation = validateQuestionItem(item);
        if (validation.valid) {
          return item;
        }
      } catch (e) {}
    }
  }

  throw new Error(`Critical: Unable to generate valid unique-option question for template ${template.id}`);
}

/**
 * Non-scored practice tutorial question
 */
export function createPracticeQuestion() {
  const drawDots = (cnt) => (s) => DrawShapes.dots(cnt, s, true, '#83C8F0');
  // Row 1: 1, 2, 3
  // Row 2: 2, 3, 4
  // Row 3: 3, 4, [5]
  const cells = [
    drawDots(1), drawDots(2), drawDots(3),
    drawDots(2), drawDots(3), drawDots(4),
    drawDots(3), drawDots(4), null
  ];

  const stimulusSvg = render3x3Matrix(cells, 260);

  const options = [
    { id: 'A', svg: renderOptionSvg(drawDots(5), 76, '5 Dots'), isCorrect: true, text_ar: '5 نقاط', text_en: '5 Dots' },
    { id: 'B', svg: renderOptionSvg(drawDots(3), 76, '3 Dots'), isCorrect: false, text_ar: '3 نقاط', text_en: '3 Dots' },
    { id: 'C', svg: renderOptionSvg(drawDots(6), 76, '6 Dots'), isCorrect: false, text_ar: '6 نقاط', text_en: '6 Dots' },
    { id: 'D', svg: renderOptionSvg(drawDots(4), 76, '4 Dots'), isCorrect: false, text_ar: '4 نقاط', text_en: '4 Dots' }
  ];

  return {
    isPractice: true,
    templateId: 'practice_tutorial_sample',
    domain: 'abstract_reasoning',
    questionText_ar: 'مثال توضيحي: أي من الخيارات يكمل نمط المصفوفة المنطقية أفقيًا ورأسيًا؟',
    questionText_en: 'Sample Item: Which option logically completes the matrix pattern across rows and columns?',
    stimulusHtml: stimulusSvg,
    options,
    correctIndex: 0,
    explanation_ar: 'يزيد عدد النقاط بمقدار 1 في كل صف وعمود. الصف الأخير يبدأ بـ 3 ثم 4، لذا يحتوي المربع المفقود على 5 نقاط (A).',
    explanation_en: 'Dot count increases by 1 across each row and column. The bottom row follows 3 then 4, so the missing cell contains 5 dots (A).'
  };
}
